import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { AddressSuggestions, DaDataAddress, DaDataSuggestion } from 'react-dadata';
import { createPortal } from 'react-dom';
import { Controller, useForm } from 'react-hook-form';
import Skeleton from 'react-loading-skeleton';
import { useLocation } from 'react-router';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useGetNumberPurchaseData, useGetUser, usePurchaseNumber, useReserveNumber } from '@/api';
import { ActivationTypes } from '@/api/enums/ActivationTypes.ts';
import { NumberPurchaseBody, ReservationResponse } from '@/api/types';
import { QuestionMarkIcon } from '@/components/icons/input';
import { ArrowDownIcon, ArrowIcon } from '@/components/icons/main-block';
import { CloseIcon } from '@/components/icons/modal';
import { Loader } from '@/components/Loader/Loader.tsx';
import { Button } from '@/components/shared/Button';
import { Collapsible } from '@/components/shared/Collapsible/Collapsible.tsx';
import { DeviceBadge } from '@/components/shared/DeviceBadge/DeviceBadge.tsx';
import { Dropdown } from '@/components/shared/Dropdown/Dropdown.tsx';
import { FileInput } from '@/components/shared/FileInput/FileInput.tsx';
import { Input } from '@/components/shared/Input/Input.tsx';
import { LinkWithPrevLocation } from '@/components/shared/LinkWithPrevLocation/LinkWithPrevLocation.tsx';
import {
    invalidErrors,
    PurchaseNumberCourierSchema,
    PurchaseNumberEsimSchema,
    PurchaseNumberMailSchema,
} from '@/components/shared/Modal/content/purchase-number/constants.ts';
import { ObtainingMethod } from '@/components/shared/Modal/content/purchase-number/ObtainingMethod/ObtainingMethod.tsx';
import { RegionBadge } from '@/components/shared/RegionBadge/RegionBadge.tsx';
import { Tooltip } from '@/components/shared/Tooltip/Tooltip.tsx';
import { numberMask, numberPlaceholder, strFormatChars, strMask } from '@/constants';
import { useYMOnMount } from '@/hooks';
import { appRoutes } from '@/routes/app-routes.ts';
import { useChosenNumberStore, useModalStore } from '@/store';
import { DeliveryMethod, DropdownOption } from '@/types';
import { formatNumber, sendYMGoal } from '@/utils';

import cn from './styles.module.sass';

export type PurchaseNumberFormData = {
    lastname: string;
    firstname: string;
    patronymic: string;
    deliveryAddressJson: string;
    deliveryDate: DropdownOption<string> | string;
    deliveryTimeRangeId: DropdownOption<number> | string;
    contactMsisdn: string;
    email: string;
};

export const PurchaseNumberContent = () => {
    const { state } = useLocation();
    const queryClient = useQueryClient();
    const { data: userData } = useGetUser();
    const {
        setIsPurchaseSuccessOpen,
        setIsPurchaseOpen,
        setIsPurchaseErrorOpen,
        setIsYooMoneyError,
        setIsErrorOpen,
    } = useModalStore();
    const {
        chosenNumber,
        clearOnUnmount,
        activeTariffSid: storeActiveTariffSid,
        deliveryMethod,
        setDeliveryMethod,
    } = useChosenNumberStore();

    const [isTooltipShown, setIsTooltipShown] = useState(false);
    const [showDeliveryTime, setShowDeliveryTime] = useState(true);
    const [passportMainImg, setPassportMainImg] = useState<File | null>(null);
    const [passportRegistrationImg, setPassportRegistrationImg] = useState<File | null>(null);
    const [currentSuggestion, setCurrentSuggestion] = useState<DaDataSuggestion<DaDataAddress>>();
    const [checkout, setCheckout] = useState();
    const [isPaymentFormShown, setIsPaymentFormShown] = useState(false);
    const [isWaitingForPayment, setIsWaitingForPayment] = useState(false);
    const [preliminaryReservedMsisdn, setPreliminaryReservedMsisdn] = useState(() =>
        localStorage.getItem('preliminaryReservedMsisdn')
    );

    const { control, formState, setError, setValue, handleSubmit, clearErrors } =
        useForm<PurchaseNumberFormData>({
            defaultValues: {
                lastname: '',
                firstname: '',
                patronymic: '',
                deliveryAddressJson: '',
                deliveryDate: '',
                deliveryTimeRangeId: '',
                contactMsisdn: '',
                email: '',
            },
            resolver: zodResolver(
                deliveryMethod === 'by-courier'
                    ? PurchaseNumberCourierSchema.omit({
                          deliveryTimeRangeId: !showDeliveryTime ? true : undefined,
                      })
                    : deliveryMethod === 'by-mail'
                      ? PurchaseNumberMailSchema
                      : PurchaseNumberEsimSchema
            ),
            mode: 'onSubmit',
        });

    const handleToggleTooltip = () => {
        setIsTooltipShown(!isTooltipShown);
    };

    const handleChangeDeliveryMethod = (method: DeliveryMethod) => {
        setDeliveryMethod(method);
        clearErrors('deliveryAddressJson');

        if (
            currentSuggestion &&
            currentSuggestion.data.region_iso_code !== 'RU-MOW' &&
            currentSuggestion.data.region_iso_code !== 'RU-MOS' &&
            method === 'by-courier'
        ) {
            setError('deliveryAddressJson', {
                message:
                    'Этот способ получения недоступен для выбранного адреса. Выберите другой способ',
            });
        }
    };

    const handleAddressChange = (suggestion?: DaDataSuggestion<DaDataAddress>) => {
        if (suggestion) {
            setValue('deliveryAddressJson', suggestion.unrestricted_value);
            setCurrentSuggestion(suggestion);

            if (suggestion.data.region_iso_code === 'RU-MOS' && deliveryMethod === 'by-courier') {
                setShowDeliveryTime(false);
            } else {
                setShowDeliveryTime(true);
            }

            if (
                suggestion.data.region_iso_code !== 'RU-MOW' &&
                suggestion.data.region_iso_code !== 'RU-MOS' &&
                deliveryMethod === 'by-courier'
            ) {
                setError('deliveryAddressJson', {
                    message:
                        'Этот способ получения недоступен для выбранного адреса. Выберите другой способ',
                });
            }
        }
    };

    const activeTariffSid = storeActiveTariffSid ?? state?.activeTariffSid;

    const { operator, number, tariff, deviceType, region, tariffMin, tariffInternet, tariffSms } =
        chosenNumber || state?.chosenNumber || {};

    const formattedNumber =
        parseInt(preliminaryReservedMsisdn || '') || formatNumber(number || '', true);

    const {
        data: numberPurchaseData,
        isLoading,
        isError,
    } = useGetNumberPurchaseData(
        formattedNumber,
        activeTariffSid,
        isWaitingForPayment ? 3000 : undefined
    );
    const { mutateAsync: purchaseNumber, isPending: isPurchasePending } =
        usePurchaseNumber(formattedNumber);
    const { mutateAsync: reserveNumber, isPending: isReservationPending } =
        useReserveNumber(formattedNumber);

    const minutes = tariffMin ? `${tariffMin} мин` : '';
    const traffic = tariffInternet ? `, ${tariffInternet}` : '';
    const sms = tariffSms ? `, ${tariffSms} смс` : '';
    const optionsDescription = (minutes + traffic + sms).replace(/^, /, '');

    const description = (
        <div className={cn.description}>
            <span>{tariff}</span>
            <span>{optionsDescription}</span>
        </div>
    );

    const rightContent = (
        <div className={cn.rightContent}>
            <RegionBadge region={region} /> <DeviceBadge device={deviceType} />{' '}
        </div>
    );

    const AddressInput = useMemo(
        () => (
            <AddressSuggestions
                suggestionsClassName={clsx('react-dadata__suggestions', cn.reactDadataSuggestions)}
                suggestionClassName={clsx('react-dadata__suggestion', cn.reactDataSuggestion)}
                token={userData?.daDataToken || ''}
                delay={300}
                onChange={handleAddressChange}
                customInput={({
                    onChange: daDataOnChange,
                    onFocus: daDataOnFocus,
                    onBlur: daDataOnBlur,
                    onKeyDown: daDataOnKeyDown,
                }) => (
                    <Controller
                        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                            <Input
                                label="Адрес (город, улица, дом, квартира)*"
                                placeholder="Ввести адрес доставки"
                                value={value}
                                autoComplete="off"
                                onChange={e => {
                                    onChange(e);
                                    daDataOnChange(e);
                                    e.target.focus();
                                }}
                                onBlur={() => {
                                    onBlur();
                                    daDataOnBlur();
                                }}
                                onFocus={daDataOnFocus}
                                onKeyDown={daDataOnKeyDown}
                                error={!!error?.message}
                                helperText={error?.message}
                                onClear={e => {
                                    e.preventDefault();
                                    clearErrors('deliveryAddressJson');
                                    setValue('deliveryAddressJson', '');
                                }}
                            />
                        )}
                        name="deliveryAddressJson"
                        control={control}
                    />
                )}
            />
        ),
        [userData?.daDataToken, deliveryMethod, currentSuggestion]
    );

    const onSubmit = async (data: PurchaseNumberFormData) => {
        try {
            if (passportMainImg && passportRegistrationImg) {
                let reservationData: ReservationResponse | null = null;

                if (numberPurchaseData?.status !== 'reserved') {
                    reservationData = await reserveNumber({
                        durationType: 'preliminary',
                        selectedTariff: activeTariffSid,
                    });
                }

                if (
                    (reservationData?.success || numberPurchaseData?.status === 'reserved') &&
                    activeTariffSid
                ) {
                    const reqBody: Omit<NumberPurchaseBody, 'documents[]'> = {
                        firstname: data.firstname,
                        lastname: data.lastname,
                        patronymic: deliveryMethod === 'by-mail' ? data.patronymic : undefined,
                        email: data.email,
                        deliveryAddressJson:
                            deliveryMethod !== 'esim'
                                ? JSON.stringify(currentSuggestion?.data)
                                : undefined,
                        deliveryDate:
                            deliveryMethod === 'by-courier' && typeof data.deliveryDate !== 'string'
                                ? data.deliveryDate.value
                                : undefined,
                        deliveryTimeRangeId:
                            deliveryMethod === 'by-courier' &&
                            showDeliveryTime &&
                            typeof data.deliveryTimeRangeId !== 'string'
                                ? data.deliveryTimeRangeId.value
                                : undefined,
                        activationTypeId: ActivationTypes[deliveryMethod],
                        selectedTariff: activeTariffSid,
                        contactMsisdn: formatNumber(data.contactMsisdn),
                    };

                    const formData = new FormData();

                    for (const key in reqBody) {
                        const value = reqBody[key as keyof typeof reqBody];
                        if (value) {
                            formData.append(key, String(value));
                        }
                    }

                    formData.append('documents[]', passportMainImg, 'passport0.jpeg');
                    formData.append('documents[]', passportRegistrationImg, 'passport1.jpeg');

                    const purchaseRes = await purchaseNumber(formData);

                    if (!purchaseRes.success && purchaseRes.errors) {
                        for (const error of purchaseRes.errors) {
                            if (error.property in invalidErrors) {
                                setError(error.property as keyof PurchaseNumberFormData, {
                                    message:
                                        invalidErrors[error.property as keyof typeof invalidErrors],
                                });
                            }

                            if (error.property === 'activationTypeId') {
                                setError('root', {
                                    type: 'activationTypeId',
                                    message: error.message,
                                });
                            }

                            if (error.property === 'documents') {
                                setError('root', {
                                    type: 'documents',
                                    message:
                                        error.type === 'identical_files'
                                            ? 'Одинаковые файлы'
                                            : 'Выбери другие файлы',
                                });
                            }

                            if (
                                error.property === 'tariff' &&
                                error.type === 'tariff_not_compatible'
                            ) {
                                setError('root', {
                                    type: error.type,
                                    message: 'Несовместимый тариф',
                                });
                            }

                            if (error.type === 'number_already_reserved') {
                                setError('root', {
                                    type: error.type,
                                    message: 'Номер уже зарезервирован',
                                });
                            }

                            if (error.type === 'number_not_reserved') {
                                setError('root', {
                                    type: error.type,
                                    message: 'Номер не был предварительно зарезервирован',
                                });
                            }
                        }
                    }

                    if (purchaseRes.success) {
                        sendYMGoal('to_payment');

                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        const checkout = new YooMoneyCheckoutWidget({
                            confirmation_token: purchaseRes.data?.confirmationToken,
                            error_callback: () => {
                                if (checkout) {
                                    checkout.destroy();
                                }
                                setIsYooMoneyError(true);
                                setIsPurchaseErrorOpen(true);
                            },
                        });

                        setCheckout(checkout);

                        await checkout.render('payment-form');
                        setIsPaymentFormShown(true);
                        localStorage.setItem('preliminaryReservedMsisdn', String(formattedNumber));
                        await queryClient.invalidateQueries({
                            queryKey: ['purchase-data', formattedNumber],
                        });
                    }
                }
            } else {
                setError('root', {
                    type: 'documents',
                    message: 'Не выбраны файлы',
                });
            }
        } catch (err) {
            if (err instanceof Error) {
                console.error(err.name, err.message);
            } else {
                console.error(err);
            }
        }
    };

    const handleClosePaymentForm = (removeFromLS: boolean) => {
        if (checkout) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            checkout.destroy();
        }

        if (removeFromLS) localStorage.removeItem('preliminaryReservedMsisdn');

        setIsPurchaseOpen(false);
        setIsPaymentFormShown(false);
        clearOnUnmount();
    };

    const handleFileChange = () => {
        clearErrors('root');
    };

    useEffect(() => {
        if (isError) {
            setIsWaitingForPayment(false);
            setPreliminaryReservedMsisdn('');
            handleClosePaymentForm(true);
            setIsErrorOpen(true);
            return;
        }

        if (preliminaryReservedMsisdn) {
            setIsWaitingForPayment(true);
        }

        if (numberPurchaseData?.status === 'waiting_for_payment') {
            setIsWaitingForPayment(true);

            if (!checkout && numberPurchaseData.paymentConfirmationToken) {
                (async () => {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    const checkout = new YooMoneyCheckoutWidget({
                        confirmation_token: numberPurchaseData.paymentConfirmationToken,
                        error_callback: () => {
                            if (checkout) {
                                checkout.destroy();
                            }
                            setIsYooMoneyError(true);
                            setIsPurchaseErrorOpen(true);
                        },
                    });

                    setCheckout(checkout);

                    await checkout.render('payment-form');
                    setIsPaymentFormShown(true);
                    localStorage.setItem('preliminaryReservedMsisdn', String(formattedNumber));
                    await queryClient.invalidateQueries({
                        queryKey: ['purchase-data', formattedNumber],
                    });
                })();
            }
        }

        if (
            isWaitingForPayment &&
            numberPurchaseData &&
            numberPurchaseData.status !== 'waiting_for_payment'
        ) {
            if (numberPurchaseData.status === 'payment_succeeded') {
                setIsPurchaseSuccessOpen(true);
                clearOnUnmount();
            } else {
                localStorage.removeItem('preliminaryReservedMsisdn');
            }

            setPreliminaryReservedMsisdn(null);
            setIsWaitingForPayment(false);
            handleClosePaymentForm(false);
        }
    }, [formattedNumber, isWaitingForPayment, numberPurchaseData, isError]);

    useYMOnMount('show_window');

    return (
        <>
            {preliminaryReservedMsisdn ? (
                <Loader fullScreen />
            ) : (
                <div className={cn.buyNumber}>
                    <div className={cn.modalHeader}>
                        <h2>Покупка номера</h2>
                    </div>
                    <LinkWithPrevLocation
                        onClick={handleClosePaymentForm.bind(null, false)}
                        to={`/${appRoutes.profile}/${appRoutes.loyaltyProgram}`}
                        state={{
                            chosenNumber,
                            activeTariffSid,
                            wasPurchaseOpen: true,
                        }}
                    >
                        <div className={cn.linkContent}>
                            <div className={cn.linkText}>
                                После оплаты этого номера
                                <br />
                                ты получишь 100 Б
                            </div>
                            <span className={cn.arrowIcon}>{ArrowIcon}</span>
                            <img
                                className={cn.phonePointsIcon}
                                src="/images/icons/phone_points.svg"
                                alt=""
                            />
                            <div className={cn.pointIcons}>
                                <img src="/images/icons/b_letter.svg" alt="" />
                                <img src="/images/icons/b_letter.svg" alt="" />
                                <img src="/images/icons/b_letter.svg" alt="" />
                                <img src="/images/icons/b_letter.svg" alt="" />
                                <img src="/images/icons/b_letter.svg" alt="" />
                            </div>
                        </div>
                    </LinkWithPrevLocation>
                    <Collapsible
                        operator={operator}
                        title={number}
                        description={description}
                        isNumberInTitle
                        isExpandable={false}
                        isArrowShown={false}
                        rightContent={rightContent}
                    />
                    {(formState.errors.root?.type === 'tariff_not_compatible' ||
                        formState.errors.root?.type === 'number_already_reserved' ||
                        formState.errors.root?.type === 'number_not_reserved') && (
                        <p className={cn.errorText}>{formState.errors.root.message}</p>
                    )}
                    <div className={cn.formContainer}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className={cn.buyNumberBlock}>
                                <h3 className={cn.blockName}>Способ получения</h3>
                                <div>
                                    <Swiper
                                        className={cn.swiper}
                                        spaceBetween={4}
                                        slidesPerView="auto"
                                    >
                                        <SwiperSlide className={cn.swiperSlide}>
                                            <ObtainingMethod
                                                name="Курьером для Москвы и МО"
                                                description="Доставка SIM-карты по адресу"
                                                onClick={handleChangeDeliveryMethod.bind(
                                                    null,
                                                    'by-courier'
                                                )}
                                                isActive={deliveryMethod === 'by-courier'}
                                            />
                                        </SwiperSlide>
                                        <SwiperSlide className={cn.swiperSlide}>
                                            <ObtainingMethod
                                                name="Почтой России для других регионов"
                                                description="Доставка SIM-карты в почтовое отделение"
                                                onClick={handleChangeDeliveryMethod.bind(
                                                    null,
                                                    'by-mail'
                                                )}
                                                isActive={deliveryMethod === 'by-mail'}
                                            />
                                        </SwiperSlide>
                                        <SwiperSlide className={cn.swiperSlide}>
                                            <ObtainingMethod
                                                name="eSIM (активация по QR-коду сразу"
                                                description="Проверьте поддержку eSIM устройством"
                                                onClick={handleChangeDeliveryMethod.bind(
                                                    null,
                                                    'esim'
                                                )}
                                                isActive={deliveryMethod === 'esim'}
                                            />
                                        </SwiperSlide>
                                    </Swiper>
                                    {formState.errors.root?.type === 'activationTypeId' && (
                                        <p className={cn.errorText}>
                                            {formState.errors.root.message}
                                        </p>
                                    )}
                                </div>
                                {deliveryMethod === 'esim' && (
                                    <a
                                        target="_blank"
                                        href="https://esimplus.me/ru/esim-supported-devices"
                                        className={cn.esimCheck}
                                        rel="noreferrer"
                                    >
                                        Проверка поддержки eSIM {ArrowDownIcon}
                                    </a>
                                )}
                            </div>
                            {(deliveryMethod === 'by-courier' || deliveryMethod === 'by-mail') && (
                                <div className={cn.buyNumberBlock}>
                                    <h3 className={cn.blockName}>Данные абонента</h3>
                                    <div className={cn.deliveryDataInputs}>
                                        <Controller
                                            render={({
                                                field: { value, onChange, onBlur },
                                                fieldState: { error },
                                            }) => (
                                                <Input
                                                    label="Фамилия*"
                                                    placeholder="Ввести фамилию"
                                                    value={value}
                                                    onChange={onChange}
                                                    onBlur={onBlur}
                                                    error={!!error?.message}
                                                    helperText={error?.message}
                                                    maskChar=""
                                                    mask={strMask}
                                                    formatChars={strFormatChars}
                                                    onClear={e => {
                                                        e.preventDefault();
                                                        clearErrors('lastname');
                                                        setValue('lastname', '');
                                                    }}
                                                />
                                            )}
                                            name="lastname"
                                            control={control}
                                        />
                                        <Controller
                                            render={({
                                                field: { value, onChange, onBlur },
                                                fieldState: { error },
                                            }) => (
                                                <Input
                                                    label="Имя*"
                                                    placeholder="Ввести имя"
                                                    value={value}
                                                    onChange={onChange}
                                                    onBlur={onBlur}
                                                    error={!!error?.message}
                                                    helperText={error?.message}
                                                    maskChar=""
                                                    mask={strMask}
                                                    formatChars={strFormatChars}
                                                    onClear={e => {
                                                        e.preventDefault();
                                                        clearErrors('firstname');
                                                        setValue('firstname', '');
                                                    }}
                                                />
                                            )}
                                            name="firstname"
                                            control={control}
                                        />
                                        {deliveryMethod === 'by-mail' && (
                                            <Controller
                                                render={({
                                                    field: { value, onChange, onBlur },
                                                    fieldState: { error },
                                                }) => (
                                                    <Input
                                                        label="Отчество*"
                                                        placeholder="Ввести отчество"
                                                        value={value}
                                                        onChange={onChange}
                                                        onBlur={onBlur}
                                                        error={!!error?.message}
                                                        helperText={error?.message}
                                                        maskChar=""
                                                        mask={strMask}
                                                        formatChars={strFormatChars}
                                                        onClear={e => {
                                                            e.preventDefault();
                                                            clearErrors('patronymic');
                                                            setValue('patronymic', '');
                                                        }}
                                                    />
                                                )}
                                                name="patronymic"
                                                control={control}
                                            />
                                        )}
                                        {AddressInput}
                                    </div>
                                </div>
                            )}
                            {deliveryMethod === 'by-courier' && (
                                <div className={cn.buyNumberBlock}>
                                    <h3 className={cn.blockName}>Дата и время доставки</h3>
                                    <div className={cn.deliveryDateInputs}>
                                        {isLoading ? (
                                            <Skeleton
                                                containerClassName={cn.skeletonContainer}
                                                height={38}
                                                count={2}
                                            />
                                        ) : (
                                            <>
                                                <Controller
                                                    render={({
                                                        field: { value, onChange, onBlur },
                                                        fieldState: { error },
                                                    }) => (
                                                        <Dropdown
                                                            options={
                                                                numberPurchaseData?.convertedData
                                                                    .dates
                                                            }
                                                            isClearable
                                                            placeholder="Дата доставки"
                                                            classNames={{
                                                                container: () =>
                                                                    cn.deliveryDateContainer,
                                                                control: () =>
                                                                    clsx(
                                                                        cn.deliveryDateControl,
                                                                        error?.message &&
                                                                            cn.deliveryDateError
                                                                    ),
                                                                menu: () => cn.deliveryDateMenu,
                                                            }}
                                                            value={value}
                                                            onChange={onChange}
                                                            onBlur={onBlur}
                                                        />
                                                    )}
                                                    name="deliveryDate"
                                                    control={control}
                                                />
                                                {showDeliveryTime && (
                                                    <Controller
                                                        render={({
                                                            field: { value, onChange, onBlur },
                                                            fieldState: { error },
                                                        }) => (
                                                            <Dropdown
                                                                options={
                                                                    numberPurchaseData
                                                                        ?.convertedData.times
                                                                }
                                                                isClearable
                                                                placeholder="Время доставки"
                                                                classNames={{
                                                                    container: () =>
                                                                        cn.deliveryDateContainer,
                                                                    control: () =>
                                                                        clsx(
                                                                            cn.deliveryDateControl,
                                                                            error?.message &&
                                                                                cn.deliveryDateError
                                                                        ),
                                                                    menu: () => cn.deliveryDateMenu,
                                                                }}
                                                                value={value}
                                                                onChange={onChange}
                                                                onBlur={onBlur}
                                                            />
                                                        )}
                                                        name="deliveryTimeRangeId"
                                                        control={control}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                    {deliveryMethod === 'by-courier' && (
                                        <div className={cn.dateHelperText}>
                                            В праздничные дни дата доставки может измениться
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className={cn.buyNumberBlock}>
                                <h3 className={cn.blockName}>
                                    {deliveryMethod === 'esim' ? 'Данные' : 'Контактные данные'}{' '}
                                    абонента
                                </h3>
                                <div className={cn.deliveryDataInputs}>
                                    <Controller
                                        render={({
                                            field: { value, onChange, onBlur },
                                            fieldState: { error },
                                        }) => (
                                            <Input
                                                label="Номер телефона*"
                                                placeholder={numberPlaceholder}
                                                mask={numberMask}
                                                type="tel"
                                                value={value}
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                error={!!error?.message}
                                                helperText={error?.message}
                                                onClear={e => {
                                                    e.preventDefault();
                                                    clearErrors('contactMsisdn');
                                                    setValue('contactMsisdn', '');
                                                }}
                                            />
                                        )}
                                        name="contactMsisdn"
                                        control={control}
                                    />
                                    {deliveryMethod === 'esim' && (
                                        <Controller
                                            render={({
                                                field: { value, onChange, onBlur },
                                                fieldState: { error },
                                            }) => (
                                                <Input
                                                    label="E-mail*"
                                                    type="email"
                                                    value={value}
                                                    onChange={onChange}
                                                    onBlur={onBlur}
                                                    error={!!error?.message}
                                                    helperText={error?.message}
                                                    onClear={e => {
                                                        e.preventDefault();
                                                        clearErrors('email');
                                                        setValue('email', '');
                                                    }}
                                                />
                                            )}
                                            name="email"
                                            control={control}
                                        />
                                    )}
                                </div>
                            </div>

                            <div className={cn.buyNumberBlock}>
                                <h3 className={cn.blockName}>
                                    Паспорт абонента*{' '}
                                    <span className={cn.tooltip} onClick={handleToggleTooltip}>
                                        <span className={cn.qMarkIcon}>{QuestionMarkIcon}</span>
                                        {isTooltipShown && (
                                            <Tooltip
                                                text={
                                                    'Активация возможна только на российский паспорт.\n' +
                                                    'Убедись, что фото хорошо читаются, видно все цифры.\n' +
                                                    'Некорректные фотографии будут отклонены по результатам ручной проверки.'
                                                }
                                            />
                                        )}
                                    </span>
                                </h3>
                                <div className={cn.fileInputs}>
                                    <FileInput
                                        name="Главный разворот"
                                        imgSrc="/images/icons/passport/passport1.svg"
                                        onFileCompression={setPassportMainImg}
                                        onChange={handleFileChange}
                                    />
                                    <FileInput
                                        name="Страница прописки"
                                        imgSrc="/images/icons/passport/passport2.svg"
                                        onFileCompression={setPassportRegistrationImg}
                                        onChange={handleFileChange}
                                    />
                                </div>
                                {formState.errors.root?.type === 'documents' && (
                                    <p className={cn.errorText}>{formState.errors.root.message}</p>
                                )}
                            </div>
                            <div className={cn.buyNumberBlock}>
                                <div className={cn.paymentTotal}>
                                    <h3 className={cn.blockName}>К оплате</h3>
                                    <span>
                                        {numberPurchaseData?.cost ? (
                                            `${numberPurchaseData.cost.subscription + numberPurchaseData.cost.activation} ₽`
                                        ) : (
                                            <Skeleton width={70} />
                                        )}
                                    </span>
                                </div>

                                <div className={cn.paymentDetails}>
                                    <div className={cn.monthlyPayment}>
                                        <span>Абон. плата / мес.</span>
                                        <span>
                                            {numberPurchaseData?.cost ? (
                                                `${numberPurchaseData.cost.subscription} ₽`
                                            ) : (
                                                <Skeleton width={40} />
                                            )}
                                        </span>
                                    </div>
                                    <div className={cn.activationFee}>
                                        <span>Стоимость подключения</span>
                                        <span>
                                            {numberPurchaseData?.cost ? (
                                                `${numberPurchaseData.cost.activation} ₽`
                                            ) : (
                                                <Skeleton width={40} />
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {isPurchasePending || isReservationPending ? (
                                <Skeleton height={48} />
                            ) : (
                                <Button type="submit">Оплатить</Button>
                            )}
                        </form>
                    </div>
                </div>
            )}
            {createPortal(
                <div id="payment-form" style={{ display: isPaymentFormShown ? 'flex' : 'none' }}>
                    <div
                        onClick={handleClosePaymentForm.bind(null, true)}
                        className={cn.closePaymentFormBtn}
                    >
                        {CloseIcon}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};
